import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  MdStar,
  MdStarBorder,
  MdAdd,
  MdRemove,
  MdFavoriteBorder,
  MdFavorite,
  MdLocalShipping,
  MdClose,
  MdCheck,
} from 'react-icons/md';
import { FaTwitter, FaFacebook, FaInstagram } from 'react-icons/fa';
import Breadcrumb from '../components/shared/Breadcrumb';
import Carousel from '../components/shared/Carousel';
import AddToCartButton from '../components/shared/AddToCartButton';
import PopularCategories from '../components/shared/PopularCategories';
import { useCartStore } from '../store/cartStore';
import { useAuthStore } from '../store/authStore';
import {
  listSkuReviews,
  submitReview,
  deleteReview,
  BackendReview,
  ReviewListResponse,
} from '../services/reviewsApi';
import { getSku, listSkus, skuToProduct } from '../services/skusApi';
import { isFavoriteSku, toggleFavoriteSku } from '../utils/favoritesStore';
import type { Product } from '../types';
import image61 from '../assets/image61.png';
import image62 from '../assets/image62.png';
import image63 from '../assets/image63.png';
import image67 from '../assets/image67.png';
import '../styles/ProductDetailPage.css';

/* ── Static product data ── */
/* Placeholder gallery used when a SKU has no (or only one) image. */
const GALLERY_FALLBACK = [image62, image61, image67, image63];

interface SimilarItem {
  id: number;
  name: string;
  price: number;
  originalPrice: number | null;
  image: string;
  badge: string | null;
  rating: number;
  reviews: number;
  brand: string;
}

const REVIEWS = [
  {
    id: 1,
    author: 'James R.',
    date: 'March 15, 2026',
    rating: 5,
    text: 'Absolutely incredible quality. The diamond infusion makes a huge difference. Smooth draw, great flavor, and the effects are spot on. Will definitely be ordering again.',
  },
  {
    id: 2,
    author: 'Sarah M.',
    date: 'March 8, 2026',
    text: 'Best pre-roll I have ever tried. The hybrid blend is perfectly balanced and the 2 gram size is generous. Packaging was also top-notch.',
    rating: 5,
  },
  {
    id: 3,
    author: 'David K.',
    date: 'February 22, 2026',
    text: 'Great product overall. Burns evenly and the taste is clean. Only minor gripe is the price, but you do get what you pay for with High Times.',
    rating: 4,
  },
];

const PAYMENT_BADGES = ['McAfee Secure', 'Visa', 'Mastercard', 'PayPal', 'Amex'];

/* ── Helpers ── */
const renderStars = (rating: number, size?: string) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      i <= rating ? (
        <MdStar key={i} size={size} />
      ) : (
        <MdStarBorder key={i} size={size} />
      ),
    );
  }
  return stars;
};

/* ── Component ── */
const ProductDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const addItem = useCartStore((s) => s.addItem);

  // The route param `slug` is the SKU id (see `skuToProduct` in services/skusApi.ts).
  const skuId = slug && /^\d+$/.test(slug) ? parseInt(slug, 10) : null;
  const [product, setProduct] = useState<Product | null>(null);
  const [productLoading, setProductLoading] = useState(true);
  const [similar, setSimilar] = useState<SimilarItem[]>([]);

  // Fetch the SKU (and its category siblings for "Similar Products") on id change.
  useEffect(() => {
    if (!skuId) {
      setProduct(null);
      setProductLoading(false);
      return;
    }
    let cancelled = false;
    setProductLoading(true);
    setImageIndex(0);
    (async () => {
      try {
        const sku = await getSku(skuId);
        if (cancelled) return;
        const mapped = skuToProduct(sku);
        if (mapped.images.length === 0) mapped.images = GALLERY_FALLBACK;
        setProduct(mapped);
        // Similar products: other active SKUs in the same category.
        if (sku.category_id != null) {
          try {
            const page = await listSkus({ category_id: sku.category_id, limit: 6 });
            if (cancelled) return;
            setSimilar(
              page.data
                .filter((s) => s.id !== sku.id)
                .slice(0, 5)
                .map((s) => ({
                  id: s.id,
                  name: s.product_name,
                  price: s.list_price ?? s.unit_cost ?? 0,
                  originalPrice: null,
                  image: s.image_url || image67,
                  badge: null,
                  rating: 0,
                  reviews: 0,
                  brand: '',
                })),
            );
          } catch {
            if (!cancelled) setSimilar([]);
          }
        } else {
          setSimilar([]);
        }
      } catch {
        if (!cancelled) setProduct(null);
      } finally {
        if (!cancelled) setProductLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [skuId]);

  const [imageIndex, setImageIndex] = useState(0);
  const [autoSlide, setAutoSlide] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [wishlisted, setWishlisted] = useState(false);

  // VP-CUST-002: favorites are a client-side/localStorage toggle (no backend
  // entity yet — see utils/favoritesStore.ts). Hydrate once the SKU id is
  // known so the PDP heart icon and the catalog's Favorites filter agree.
  useEffect(() => {
    if (skuId != null) setWishlisted(isFavoriteSku(skuId));
  }, [skuId]);

  const handleToggleWishlist = () => {
    if (skuId == null) return;
    setWishlisted(toggleFavoriteSku(skuId));
  };
  const [activeTab, setActiveTab] = useState<'description' | 'info' | 'reviews'>('description');
  const [addedToCart, setAddedToCart] = useState(false);

  /* ── Reviews (live from backend) ──
     The route param `slug` is the SKU id (see `skuToProduct` in services/skusApi.ts).
     We pull /skus/:id/reviews on tab open and offer logged-in customers a
     write form. The legacy REVIEWS mock above is kept only as a fallback
     when the backend call hasn't returned yet — we never overwrite real
     data with it. */
  const user = useAuthStore((s) => s.user);
  const skuIdFromSlug = skuId;
  const [reviewsData, setReviewsData] = useState<ReviewListResponse | null>(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState<string | null>(null);
  const [myRating, setMyRating] = useState(5);
  const [myTitle, setMyTitle] = useState('');
  const [myComment, setMyComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  const refreshReviews = useCallback(async () => {
    if (!skuIdFromSlug) return;
    setReviewsLoading(true);
    setReviewsError(null);
    try {
      const r = await listSkuReviews(skuIdFromSlug, { limit: 25 });
      setReviewsData(r);
    } catch (err) {
      setReviewsError(err instanceof Error ? err.message : 'Failed to load reviews');
    } finally {
      setReviewsLoading(false);
    }
  }, [skuIdFromSlug]);

  // Load reviews when the user opens the reviews tab (lazy — not on every PDP mount).
  useEffect(() => {
    if (activeTab === 'reviews' && skuIdFromSlug && !reviewsData) {
      refreshReviews();
    }
  }, [activeTab, skuIdFromSlug, reviewsData, refreshReviews]);

  const myExistingReview: BackendReview | undefined =
    reviewsData?.data.find((r) => user && String(r.customer_id) === String(user.id));

  // Pre-populate the form when editing an existing review.
  useEffect(() => {
    if (myExistingReview) {
      setMyRating(myExistingReview.rating);
      setMyTitle(myExistingReview.title ?? '');
      setMyComment(myExistingReview.comment ?? '');
    }
  }, [myExistingReview?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!skuIdFromSlug) return;
    setSubmittingReview(true);
    try {
      await submitReview(skuIdFromSlug, {
        rating: myRating,
        title: myTitle.trim() || undefined,
        comment: myComment.trim() || undefined,
      });
      await refreshReviews();
    } catch (err) {
      setReviewsError(err instanceof Error ? err.message : 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleDeleteMyReview = async () => {
    if (!skuIdFromSlug || !myExistingReview) return;
    try {
      await deleteReview(skuIdFromSlug, myExistingReview.id);
      setMyRating(5); setMyTitle(''); setMyComment('');
      await refreshReviews();
    } catch (err) {
      setReviewsError(err instanceof Error ? err.message : 'Failed to delete review');
    }
  };

  const galleryImages = product?.images ?? [];
  const mainImage = galleryImages[imageIndex];

  const nextImage = useCallback(() => {
    const len = galleryImages.length;
    if (len === 0) return;
    setImageIndex((prev) => (prev + 1) % len);
  }, [galleryImages.length]);

  // Auto-slide every 3 seconds
  useEffect(() => {
    if (!autoSlide) return;
    const timer = setInterval(nextImage, 3000);
    return () => clearInterval(timer);
  }, [autoSlide, nextImage]);

  const handleAddToCart = () => {
    if (!product) return;
    addItem(product, quantity);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const handleQtyChange = (delta: number) => {
    setQuantity((prev) => Math.max(1, prev + delta));
  };

  // All hooks above run unconditionally; now it's safe to short-circuit render.
  if (productLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '120px 24px', color: 'var(--gray-400)' }}>
        <p style={{ fontSize: '1.1rem' }}>Loading product…</p>
      </div>
    );
  }
  if (!product) {
    return (
      <div style={{ textAlign: 'center', padding: '120px 24px', color: 'var(--gray-500)' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: 8 }}>Product not found</h2>
        <p style={{ marginBottom: 20 }}>This product may no longer be available.</p>
        <button
          onClick={() => navigate('/products')}
          style={{ padding: '10px 22px', borderRadius: 8, border: 'none', background: '#2B7FFF', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
        >
          Browse Products
        </button>
      </div>
    );
  }

  return (
    <>
      <Breadcrumb extra={product.name} />

      {/* ── Section 1: Hero ── */}
      <section className="pdp-hero">
        <div className="pdp-hero__inner">
          {/* Left: Gallery */}
          <div
            className="pdp-gallery"
            onMouseEnter={() => setAutoSlide(false)}
            onMouseLeave={() => setAutoSlide(true)}
          >
            <div className="pdp-gallery__main">
              {product.isNewArrival && <span className="pdp-gallery__badge">New</span>}
              <img key={imageIndex} src={mainImage} alt={product.name} className="pdp-gallery__main-img" />
              {/* Slide indicators */}
              <div className="pdp-gallery__indicators">
                {product.images.map((_, i) => (
                  <button
                    key={i}
                    className={`pdp-gallery__indicator ${i === imageIndex ? 'pdp-gallery__indicator--active' : ''}`}
                    onClick={() => setImageIndex(i)}
                    aria-label={`View image ${i + 1}`}
                  />
                ))}
              </div>
              {/* Progress bar */}
              <div className="pdp-gallery__progress">
                <div
                  className={`pdp-gallery__progress-bar ${autoSlide ? 'pdp-gallery__progress-bar--running' : ''}`}
                  key={`${imageIndex}-${autoSlide}`}
                />
              </div>
            </div>

            <div className="pdp-gallery__thumbs">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  className={`pdp-gallery__thumb${i === imageIndex ? ' pdp-gallery__thumb--active' : ''}`}
                  onClick={() => setImageIndex(i)}
                  aria-label={`View image ${i + 1}`}
                >
                  <img src={img} alt="" className="pdp-gallery__thumb-img" />
                </button>
              ))}
            </div>

            <div className="pdp-gallery__rating">
              <span className="pdp-gallery__stars">{renderStars(product.rating)}</span>
              <span className="pdp-gallery__review-count">({product.reviewCount})</span>
            </div>
          </div>

          {/* Center: Product Info */}
          <div className="pdp-info">
            <h1 className="pdp-info__name">{product.name}</h1>
            <p className="pdp-info__price">
              ${product.price.toFixed(2)}
              {product.originalPrice != null && product.originalPrice > product.price && (
                <span style={{ marginLeft: 10, fontSize: '0.7em', fontWeight: 500, color: '#9CA3AF', textDecoration: 'line-through' }}>
                  ${product.originalPrice.toFixed(2)}
                </span>
              )}
            </p>

            <ul className="pdp-info__features">
              {product.features.map((f, i) => (
                <li key={i} className="pdp-info__feature">{f}</li>
              ))}
            </ul>

            <span className="pdp-info__shipping-badge">
              <MdLocalShipping /> Free Shipping
            </span>

            <div className="pdp-info__stock">
              <span className="pdp-info__stock-dot" />
              In stock
            </div>

            <div className="pdp-info__qty-row">
              <span className="pdp-info__qty-label">qty</span>
              <div className="pdp-info__qty-control">
                <button className="pdp-info__qty-btn" onClick={() => handleQtyChange(-1)} aria-label="Decrease quantity">
                  <MdRemove />
                </button>
                <input
                  type="number"
                  className="pdp-info__qty-input"
                  value={quantity}
                  min={1}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                />
                <button className="pdp-info__qty-btn" onClick={() => handleQtyChange(1)} aria-label="Increase quantity">
                  <MdAdd />
                </button>
              </div>
            </div>

            <div className="pdp-info__actions">
              <button className={`pdp-info__add-to-cart${addedToCart ? ' pdp-info__add-to-cart--added' : ''}`} onClick={handleAddToCart}>
                {addedToCart ? '✓ Added to Cart' : 'Add to Cart'}
              </button>
              <button
                className={`pdp-info__wishlist-btn${wishlisted ? ' pdp-info__wishlist-btn--active' : ''}`}
                onClick={handleToggleWishlist}
                aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
              >
                {wishlisted ? <MdFavorite /> : <MdFavoriteBorder />}
              </button>
            </div>

            <div className="pdp-info__checkout-safe">
              <span className="pdp-info__checkout-label">Guaranteed Safe Checkout</span>
              <div className="pdp-info__payment-badges">
                {PAYMENT_BADGES.map((b) => (
                  <span key={b} className="pdp-info__payment-badge">{b}</span>
                ))}
              </div>
            </div>

            <div className="pdp-info__meta">
              <div className="pdp-info__meta-row">
                <span className="pdp-info__meta-label">SKU:</span>
                {product.sku}
              </div>
              <div className="pdp-info__meta-row">
                <span className="pdp-info__meta-label">Category:</span>
                <Link to="/categories" className="pdp-info__meta-link">{product.category}</Link>
              </div>
              <div className="pdp-info__meta-row">
                <span className="pdp-info__meta-label">Tags:</span>
                <span className="pdp-info__tags">
                  {product.tags.map((tag, i) => (
                    <React.Fragment key={tag}>
                      <span className="pdp-info__tag">{tag}</span>
                      {i < product.tags.length - 1 && ', '}
                    </React.Fragment>
                  ))}
                </span>
              </div>
            </div>

            <div className="pdp-info__social">
              <button className="pdp-info__social-btn" aria-label="Share on Twitter"><FaTwitter /></button>
              <button className="pdp-info__social-btn" aria-label="Share on Facebook"><FaFacebook /></button>
              <button className="pdp-info__social-btn" aria-label="Share on Instagram"><FaInstagram /></button>
            </div>
          </div>

          {/* Right: Cart Sidebar */}
          <aside className="pdp-cart">
            <h3 className="pdp-cart__title">Your Cart</h3>

            <div className="pdp-cart__item">
              <img src={image62} alt="Cart item" className="pdp-cart__item-img" />
              <div className="pdp-cart__item-details">
                <div className="pdp-cart__item-name">Hash Holes / High Times</div>
                <div className="pdp-cart__item-qty">3 x $579.00</div>
              </div>
              <button className="pdp-cart__item-remove" aria-label="Remove item">
                <MdClose />
              </button>
            </div>

            <div className="pdp-cart__subtotal">
              <span className="pdp-cart__subtotal-label">Sub Total:</span>
              <span className="pdp-cart__subtotal-price">$1,737.00</span>
            </div>

            <div className="pdp-cart__buttons">
              <Link to="/cart" className="pdp-cart__btn pdp-cart__btn--outline">View Cart</Link>
              <Link to="/checkout" className="pdp-cart__btn pdp-cart__btn--filled">Checkout</Link>
            </div>

            <div className="pdp-cart__shipping">
              <MdLocalShipping />
              <span>Ships from United States</span>
            </div>
          </aside>
        </div>
      </section>

      {/* ── Section 2: Similar Products ── */}
      {similar.length > 0 && (
      <section className="pdp-similar">
        <div className="pdp-similar__inner">
          <div className="pdp-similar__header">
            <h2 className="pdp-similar__heading">Similar Products</h2>
            <span className="pdp-similar__count">{similar.length} products</span>
          </div>
          <div className="pdp-similar__grid">
            {similar.map((p, idx) => (
              <div
                className="pdp-similar-card"
                key={p.id}
                onClick={() => navigate(`/products/${p.id}`)}
                style={{ animationDelay: `${idx * 0.08}s` }}
              >
                <div className="pdp-similar-card__image-wrapper">
                  {p.badge && (
                    <span className={`pdp-similar-card__badge pdp-similar-card__badge--${p.badge.toLowerCase()}`}>
                      {p.badge}
                    </span>
                  )}
                  <img src={p.image} alt={p.name} className="pdp-similar-card__image" />
                  <div className="pdp-similar-card__overlay">
                    <span className="pdp-similar-card__quick-view">Quick View</span>
                  </div>
                </div>
                <div className="pdp-similar-card__info">
                  <span className="pdp-similar-card__brand">{p.brand}</span>
                  <div className="pdp-similar-card__name">{p.name}</div>
                  <div className="pdp-similar-card__rating">
                    <span className="pdp-similar-card__stars">
                      {renderStars(Math.floor(p.rating), '14')}
                    </span>
                    <span className="pdp-similar-card__review-count">({p.reviews})</span>
                  </div>
                  <div className="pdp-similar-card__stock">
                    <span className="pdp-similar-card__stock-dot" />
                    In stock
                  </div>
                  <div className="pdp-similar-card__row">
                    <div className="pdp-similar-card__prices">
                      <span className="pdp-similar-card__price">${p.price.toFixed(2)}</span>
                      {p.originalPrice && (
                        <span className="pdp-similar-card__original">${p.originalPrice.toFixed(2)}</span>
                      )}
                    </div>
                    <AddToCartButton
                      onClick={(e) => {
                        e.stopPropagation();
                        addItem({
                          id: p.id, name: p.name, slug: String(p.id), price: p.price,
                          description: '', shortDescription: '', images: [p.image],
                          category: 'Cigarettes & Rolls', brand: p.brand,
                          rating: p.rating, reviewCount: p.reviews, inStock: true, stockCount: 10,
                          tags: [], features: [], sku: '',
                        }, 1);
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ── Promo Carousel ── */}
      <section className="pdp-carousel">
        <Carousel
          slides={[
            { id: 1, image: image62, tag: 'WHOLESALE DEALS', title: 'Future-Proof Your Inventory', subtitle: 'Explore premium selections and lock in wholesale pricing today.', cta: { label: 'Shop Now' }, bgColor: '#1A1040' },
            { id: 2, image: image61, tag: 'LIMITED TIME', title: 'Beverages at Bulk Prices', subtitle: 'Stock up on trending drinks with exclusive partner discounts.', cta: { label: 'View Deals' }, bgColor: '#2D1A0E' },
            { id: 3, image: image63, tag: 'ECO-FRIENDLY', title: 'Natural & Organic Range', subtitle: 'Sustainable products your customers love — delivered fast.', cta: { label: 'Explore Now' }, bgColor: '#0A2E1A' },
          ]}
          autoPlay
          interval={5000}
        />
      </section>

      {/* ── Section 3: Tabs ── */}
      <section className="pdp-tabs">
        <div className="pdp-tabs__inner">
          <nav className="pdp-tabs__nav">
            <button
              className={`pdp-tabs__tab${activeTab === 'description' ? ' pdp-tabs__tab--active' : ''}`}
              onClick={() => setActiveTab('description')}
            >
              Description
            </button>
            <button
              className={`pdp-tabs__tab${activeTab === 'info' ? ' pdp-tabs__tab--active' : ''}`}
              onClick={() => setActiveTab('info')}
            >
              Additional Information
            </button>
            <button
              className={`pdp-tabs__tab${activeTab === 'reviews' ? ' pdp-tabs__tab--active' : ''}`}
              onClick={() => setActiveTab('reviews')}
            >
              Reviews ({reviewsData?.stats.total ?? 0})
            </button>
          </nav>

          <div className="pdp-tabs__content">
            {/* Description */}
            {activeTab === 'description' && (
              <div>
                <p className="pdp-desc__text">
                  High Times THCA Diamond Infused Pre-Rolls represent the pinnacle of pre-roll craftsmanship. Each
                  pre-roll is carefully hand-crafted using only the finest hybrid flower, infused with premium THCA
                  diamonds for a potent and smooth smoking experience. The result is a luxurious, slow-burning roll that
                  delivers consistent flavor and effects from start to finish.
                </p>
                <p className="pdp-desc__text">
                  Our 2 gram hybrid pre-rolls are designed for those who appreciate quality above all else. The hybrid
                  blend combines the best of both indica and sativa genetics, providing a balanced experience that is
                  equally suited for daytime relaxation and creative inspiration. Every batch is lab-tested for purity
                  and potency, ensuring you receive a product that meets the highest industry standards.
                </p>
                <p className="pdp-desc__text">
                  Packaged in a premium airtight tube to preserve freshness, each pre-roll is ready to enjoy straight
                  out of the box. Whether you are a seasoned enthusiast or trying infused pre-rolls for the first time,
                  High Times delivers an unmatched experience.
                </p>

                <h4 className="pdp-desc__features-title">Product Features</h4>
                <ul className="pdp-desc__features">
                  <li className="pdp-desc__feature">
                    <MdCheck className="pdp-desc__feature-icon" />
                    Premium THCA diamond infusion for enhanced potency
                  </li>
                  <li className="pdp-desc__feature">
                    <MdCheck className="pdp-desc__feature-icon" />
                    Hand-crafted with top-shelf hybrid flower
                  </li>
                  <li className="pdp-desc__feature">
                    <MdCheck className="pdp-desc__feature-icon" />
                    Generous 2 gram size for extended sessions
                  </li>
                  <li className="pdp-desc__feature">
                    <MdCheck className="pdp-desc__feature-icon" />
                    Slow, even burn with smooth draw
                  </li>
                  <li className="pdp-desc__feature">
                    <MdCheck className="pdp-desc__feature-icon" />
                    Lab-tested for purity and potency
                  </li>
                  <li className="pdp-desc__feature">
                    <MdCheck className="pdp-desc__feature-icon" />
                    Airtight packaging to preserve freshness
                  </li>
                </ul>
              </div>
            )}

            {/* Additional Information */}
            {activeTab === 'info' && (
              <table className="pdp-addinfo__table">
                <tbody>
                  <tr>
                    <th>Weight</th>
                    <td>2 grams</td>
                  </tr>
                  <tr>
                    <th>Dimensions</th>
                    <td>12 x 1.5 x 1.5 cm</td>
                  </tr>
                  <tr>
                    <th>Material</th>
                    <td>Premium Hybrid Flower, THCA Diamonds</td>
                  </tr>
                  <tr>
                    <th>Pack Size</th>
                    <td>1 Pre-Roll per tube</td>
                  </tr>
                  <tr>
                    <th>Brand</th>
                    <td>High Times</td>
                  </tr>
                </tbody>
              </table>
            )}

            {/* Reviews */}
            {activeTab === 'reviews' && (
              <div>
                {/* Live aggregate header — falls back to the static REVIEWS array
                    only when the backend hasn't responded yet (e.g. first paint
                    or this URL doesn't map to a numeric SKU id). */}
                <div className="pdp-reviews__summary">
                  <span className="pdp-reviews__avg-score">
                    {reviewsData?.stats.average_rating?.toFixed(1) ?? '—'}
                  </span>
                  <div className="pdp-reviews__avg-meta">
                    <span className="pdp-reviews__avg-stars">
                      {renderStars(Math.round(reviewsData?.stats.average_rating ?? 0))}
                    </span>
                    <span className="pdp-reviews__avg-count">
                      {reviewsLoading
                        ? 'Loading reviews…'
                        : `Based on ${reviewsData?.stats.total ?? 0} review${(reviewsData?.stats.total ?? 0) === 1 ? '' : 's'}`}
                    </span>
                  </div>
                </div>

                {reviewsError && (
                  <p style={{ color: '#EF4444', background: '#FEF2F2', padding: 8, borderRadius: 6, margin: '12px 0', fontSize: 13 }}>
                    {reviewsError}
                  </p>
                )}

                {/* Write-a-review form for logged-in customers */}
                {skuIdFromSlug && user && (
                  <form
                    onSubmit={handleSubmitReview}
                    style={{ margin: '16px 0', padding: 16, background: '#F9FAFB', borderRadius: 8 }}
                  >
                    <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: '#111827' }}>
                      {myExistingReview ? 'Update your review' : 'Write a review'}
                    </h3>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          type="button"
                          key={n}
                          onClick={() => setMyRating(n)}
                          aria-label={`${n} star${n === 1 ? '' : 's'}`}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: n <= myRating ? '#FBBF24' : '#D1D5DB' }}
                        >
                          {n <= myRating ? <MdStar /> : <MdStarBorder />}
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={myTitle}
                      onChange={(e) => setMyTitle(e.target.value)}
                      placeholder="Short title (optional)"
                      maxLength={150}
                      style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #D1D5DB', marginBottom: 8, fontSize: 14 }}
                    />
                    <textarea
                      value={myComment}
                      onChange={(e) => setMyComment(e.target.value)}
                      placeholder="Tell other customers what you thought…"
                      rows={4}
                      style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #D1D5DB', marginBottom: 8, fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }}
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="submit"
                        disabled={submittingReview}
                        style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#10B981', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer', opacity: submittingReview ? 0.6 : 1 }}
                      >
                        {submittingReview ? 'Submitting…' : myExistingReview ? 'Update Review' : 'Submit Review'}
                      </button>
                      {myExistingReview && (
                        <button
                          type="button"
                          onClick={handleDeleteMyReview}
                          style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #FCA5A5', background: '#FEF2F2', color: '#B91C1C', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
                        >
                          Delete Review
                        </button>
                      )}
                    </div>
                  </form>
                )}

                <div className="pdp-reviews__list">
                  {(reviewsData?.data ?? []).length === 0 && !reviewsLoading && (
                    /* No real reviews yet — still show the legacy mock array so the
                       PDP doesn't look empty on a brand-new SKU. These won't appear
                       once a single real review is posted. */
                    REVIEWS.map((review) => (
                      <div className="pdp-review-card" key={`mock-${review.id}`}>
                        <div className="pdp-review-card__header">
                          <span className="pdp-review-card__author">{review.author}</span>
                          <span className="pdp-review-card__date">{review.date}</span>
                        </div>
                        <div className="pdp-review-card__stars">{renderStars(review.rating)}</div>
                        <p className="pdp-review-card__text">{review.text}</p>
                      </div>
                    ))
                  )}

                  {(reviewsData?.data ?? []).map((review) => (
                    <div className="pdp-review-card" key={review.id}>
                      <div className="pdp-review-card__header">
                        <span className="pdp-review-card__author">
                          {review.customer_name ?? 'Customer'}
                          {review.is_verified_purchase && (
                            <span style={{ marginLeft: 6, fontSize: 11, color: '#10B981', fontWeight: 600 }}>
                              ✓ Verified
                            </span>
                          )}
                        </span>
                        <span className="pdp-review-card__date">
                          {new Date(review.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </span>
                      </div>
                      <div className="pdp-review-card__stars">{renderStars(review.rating)}</div>
                      {review.title && (
                        <p style={{ fontWeight: 600, margin: '4px 0', color: '#111827' }}>{review.title}</p>
                      )}
                      {review.comment && <p className="pdp-review-card__text">{review.comment}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Popular Categories ── */}
      <PopularCategories />
    </>
  );
};

export default ProductDetailPage;
